// Image preloader service for better performance
export class ImagePreloader {
  private static preloadedImages = new Map<string, HTMLImageElement>();
  private static preloadPromises = new Map<string, Promise<HTMLImageElement>>();

  /**
   * Preload a single image
   */
  static preloadImage(src: string): Promise<HTMLImageElement> {
    // Return cached image if already preloaded
    if (this.preloadedImages.has(src)) {
      return Promise.resolve(this.preloadedImages.get(src)!);
    }

    // Return existing promise if already loading
    if (this.preloadPromises.has(src)) {
      return this.preloadPromises.get(src)!;
    }

    // Create new preload promise
    const promise = new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        this.preloadedImages.set(src, img);
        this.preloadPromises.delete(src);
        resolve(img);
      };
      
      img.onerror = () => {
        this.preloadPromises.delete(src);
        reject(new Error(`Failed to load image: ${src}`));
      };
      
      // Set crossOrigin to handle CORS if needed
      img.crossOrigin = 'anonymous';
      img.src = src;
    });

    this.preloadPromises.set(src, promise);
    return promise;
  }

  /**
   * Preload multiple images
   */
  static async preloadImages(sources: string[]): Promise<HTMLImageElement[]> {
    const uniqueSources = [...new Set(sources)]; // Remove duplicates
    const promises = uniqueSources.map(src => this.preloadImage(src));
    
    try {
      return await Promise.all(promises);
    } catch (error) {
      console.warn('Some images failed to preload:', error);
      // Return successfully loaded images even if some fail
      const results = await Promise.allSettled(promises);
      return results
        .filter((result): result is PromiseFulfilledResult<HTMLImageElement> => 
          result.status === 'fulfilled'
        )
        .map(result => result.value);
    }
  }

  /**
   * Preload all images from product data
   */
  static async preloadProductImages(products: Array<{ images?: string[]; variations?: Array<{ images?: string[] | Array<{ url: string }> }> }>): Promise<void> {
    const allImageUrls = new Set<string>();

    // Collect all image URLs from products
    products.forEach(product => {
      // Add main product images
      if (product.images && Array.isArray(product.images)) {
        product.images.forEach((url: string) => allImageUrls.add(url));
      }

      // Add variation-specific images
      if (product.variations && Array.isArray(product.variations)) {
        product.variations.forEach((variation) => {
          if (variation.images && Array.isArray(variation.images)) {
            variation.images.forEach((image: string | { url: string }) => {
              const url = typeof image === 'string' ? image : image.url;
              allImageUrls.add(url);
            });
          }
        });
      }
    });

    const imageUrls = Array.from(allImageUrls);
    console.log(`Preloading ${imageUrls.length} product images...`);

    try {
      const startTime = performance.now();
      await this.preloadImages(imageUrls);
      const endTime = performance.now();
      console.log(`Successfully preloaded ${imageUrls.length} images in ${Math.round(endTime - startTime)}ms`);
    } catch (error) {
      console.error('Error preloading product images:', error);
    }
  }

  /**
   * Check if an image is preloaded
   */
  static isImagePreloaded(src: string): boolean {
    return this.preloadedImages.has(src);
  }

  /**
   * Get preloaded image
   */
  static getPreloadedImage(src: string): HTMLImageElement | null {
    return this.preloadedImages.get(src) || null;
  }

  /**
   * Clear all preloaded images from memory
   */
  static clearCache(): void {
    this.preloadedImages.clear();
    this.preloadPromises.clear();
    console.log('Image cache cleared');
  }

  /**
   * Get cache statistics
   */
  static getCacheStats(): { preloaded: number; loading: number } {
    return {
      preloaded: this.preloadedImages.size,
      loading: this.preloadPromises.size
    };
  }
}